import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ChevronRight, MessageCircle } from "lucide-react";
import EnquiryForm from "../components/EnquiryForm";

const contactInfo = [
  {
    icon: <MapPin size={22} color="#F2C94C" />,
    title: "Our Location",
    lines: ["C/O Bharty Tutorials", "Kandivali East", "Mumbai, Maharashtra – 400101"],
    link: "https://maps.google.com/?q=Kandivali+East+Mumbai",
    linkText: "Get Directions",
  },
  {
    icon: <Phone size={22} color="#F2C94C" />,
    title: "Call Us",
    lines: ["+91 9867913757 (Er. Shrihari Poojary)", "+91 7977526163 (Dr. Sunny Singh)", "+91 7977678307 (Dr. Sunil Choudhary)"],
    link: "tel:9867913757",
    linkText: "Call Now",
  },
  {
    icon: <Mail size={22} color="#F2C94C" />,
    title: "Email Us",
    lines: ["tridentacademy101@gmail.com"],
    link: "mailto:tridentacademy101@gmail.com",
    linkText: "Send Email",
  },
  {
    icon: <Clock size={22} color="#F2C94C" />,
    title: "Office Hours",
    lines: ["Monday – Sunday", "8:00 AM – 8:00 PM"],
    link: null,
    linkText: null,
  },
];

export default function Contact() {
  return (
    <div data-testid="contact-page" className="pt-16 md:pt-20">
      {/* Header */}
      <section className="page-header py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: "#F2C94C" }}>
            Get In Touch
          </p>
          <h1
            className="text-4xl md:text-6xl font-extrabold text-white mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Contact Us
          </h1>
          <p className="text-blue-200 text-base md:text-lg max-w-2xl mx-auto">
            Have questions? Our team is here to help. Fill out the enquiry form or reach us directly.
          </p>
          <nav className="flex items-center justify-center gap-2 mt-6 text-xs text-blue-300">
            <Link to="/" className="hover:text-yellow-400 transition-colors">Home</Link>
            <ChevronRight size={12} />
            <span className="text-yellow-400">Contact</span>
          </nav>
        </div>
      </section>

      {/* Contact info cards */}
      <section data-testid="contact-info-section" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {contactInfo.map((info, i) => (
              <div
                key={i}
                className="rounded-2xl p-6 border border-gray-100 bg-white card-hover"
                data-testid={`contact-info-${i}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: "#0A2540" }}
                >
                  {info.icon}
                </div>
                <h3
                  className="font-bold text-base mb-3"
                  style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                >
                  {info.title}
                </h3>
                <div className="space-y-1 mb-4">
                  {info.lines.map((line, j) => (
                    <p key={j} className="text-sm text-gray-500">
                      {line}
                    </p>
                  ))}
                </div>
                {info.link && (
                  <a
                    href={info.link}
                    target={info.link.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="text-xs font-bold flex items-center gap-1 hover:gap-2 transition-all"
                    style={{ color: "#0A2540" }}
                  >
                    {info.linkText} <ChevronRight size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Map + Form */}
      <section data-testid="map-form-section" className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Map */}
            <div>
              <h2
                className="text-2xl font-extrabold mb-5"
                style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
              >
                Find Us on the Map
              </h2>
              <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-200">
                <iframe
                  title="Trident Academy Location"
                  data-testid="google-map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3767.8682015063678!2d72.8568327!3d19.200958099999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x61a6cb1d57d92b3f%3A0x97dfebc34cc01c8!2sTrident%20Academy!5e0!3m2!1sen!2sin!4v1772658175770!5m2!1sen!2sin"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div
                className="mt-5 rounded-2xl p-5 text-white flex items-start gap-4"
                style={{ backgroundColor: "#0A2540" }}
              >
                <MapPin size={20} color="#F2C94C" className="flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-sm mb-1" style={{ fontFamily: "Manrope, sans-serif" }}>
                    Trident Academy
                  </div>
                  <div className="text-blue-200 text-sm">
                    C/O Bharty Tutorials <br/>
                    Shop No. 01, Om Matru Chaya CHS, Ashok Chakravarti Road, Ashok Nagar, Kandivali East, Mumbai, Maharashtra 400 101
                  </div>
                  <div className="text-blue-300 text-xs mt-1">
                    Near Thakur House, Kandivali East.
                  </div>
                </div>
              </div>
            </div>

            {/* Enquiry Form */}
            <div>
              <div className="flex items-center gap-2 mb-5">
                <MessageCircle size={20} color="#F2C94C" />
                <h2
                  className="text-2xl font-extrabold"
                  style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
                >
                  Send an Enquiry
                </h2>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                  Fill out the form below and our team will get back to you within 24 hours with all the information you need.
                </p>
                <EnquiryForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Director direct contact */}
      <section data-testid="direct-contact-section" className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-2xl font-extrabold text-center mb-8"
            style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
          >
            Direct Contact – <span style={{ color: "#F2C94C" }}>Our Directors</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { name: "Er. Shrihari Poojary", role: "Director, Mathematics", phone: "9867913757", initials: "SP" },
              { name: "Dr. Sunny Singh", role: "Director, Biology", phone: "7977526163", initials: "SS" },
              { name: "Dr. Sunil Choudhary", role: "Director, Chemistry", phone: "7977678307", initials: "SC" },
            ].map((d, i) => (
              <div
                key={i}
                className="flex items-center gap-4 p-5 rounded-2xl border border-gray-100 card-hover bg-white"
                data-testid={`director-contact-${i}`}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-extrabold text-white flex-shrink-0"
                  style={{ backgroundColor: "#0A2540" }}
                >
                  {d.initials}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm" style={{ color: "#0A2540" }}>{d.name}</div>
                  <div className="text-xs text-gray-400 mb-2">{d.role}</div>
                  <a
                    href={`tel:${d.phone}`}
                    className="flex items-center gap-1.5 text-xs font-bold transition-all"
                    style={{ color: "#F2C94C" }}
                    data-testid={`director-phone-${i}`}
                  >
                    <Phone size={12} />
                    +91 {d.phone}
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
